import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SourcePicker } from "@/components/bi/SourcePicker";
import { ArrowLeftRight, ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";

// Replace one dashboard data source with another. The server blocks the swap until every field
// the old source feeds resolves in the new dataset (same name or an explicit mapping).
export function ReplaceSourceDialog({ open, onOpenChange, dashboardId, source, datasets, onReplaced }) {
  const [target, setTarget] = useState("");
  const [fieldMap, setFieldMap] = useState({});
  const [plan, setPlan] = useState(null);      // {ok, mapping, missing, widgets, new_columns}
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setTarget(""); setFieldMap({}); setPlan(null); } }, [open, source?.dataset_id]);

  const dryRun = async (tid, fm) => {
    if (!tid) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/dashboards/${dashboardId}/data-sources/${source.dataset_id}/replace`, { new_dataset_id: tid, field_map: fm, dry_run: true });
      setPlan({ ...data, blocked: false });
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "missing_fields") {
        // Blocked: list the missing fields; suggestions are pre-filled but only applied on an explicit re-check.
        setPlan({ ...d, blocked: true });
        const seeded = { ...fm };
        (d.missing || []).forEach((m) => { if (m.suggestion && !seeded[m.field]) seeded[m.field] = m.suggestion; });
        setFieldMap(seeded);
      } else toast.error(typeof d === "string" ? d : d?.message || "Could not check the replacement");
    } finally { setBusy(false); }
  };

  const pickTarget = (tid) => { setTarget(tid); setFieldMap({}); dryRun(tid, {}); };
  const mapField = (from, to) => setFieldMap((fm) => ({ ...fm, [from]: to }));
  const allMapped = plan?.blocked && (plan.missing || []).every((m) => fieldMap[m.field]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dashboards/${dashboardId}/data-sources/${source.dataset_id}/replace`, { new_dataset_id: target, field_map: fieldMap, dry_run: false });
      toast.success(`Source replaced — ${data.widgets?.length || 0} widget(s) now read from ${data.new_name}`);
      onReplaced(data);
      onOpenChange(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Replace failed");
      if (d?.code === "missing_fields") setPlan({ ...d, blocked: true });
    } finally { setBusy(false); }
  };

  const candidates = (datasets || []).filter((d) => d.id !== source?.dataset_id);
  const newCols = plan?.new_columns || [];
  const mappedRows = plan ? Object.entries(plan.mapping || {}) : [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="replace-source-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ArrowLeftRight className="w-5 h-5 text-[color:var(--brand-ocean)]" />Replace “{source?.name}”</DialogTitle>
          <DialogDescription>Every widget bound to this source is rewired to the new dataset. Fields must exist in the new dataset (same name or mapped below) — otherwise the replace is blocked.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-[color:var(--ink-3)] shrink-0">Replace with</span>
            <SourcePicker value={target} datasets={candidates} onChange={pickTarget} testid="replace-source-target" placeholder="Pick the new dataset…" />
          </div>
          {plan && (
            <div className="space-y-3" data-testid="replace-source-plan">
              <div className="text-xs text-[color:var(--ink-2)]">{plan.widgets?.length || 0} widget(s) affected · {mappedRows.length} field(s) resolved{plan.missing?.length ? ` · ${plan.missing.length} missing` : ""}</div>
              {plan.blocked && (
                <div data-testid="replace-source-blocked" className="rounded-lg border border-[color:var(--danger)]/40 bg-[color:rgba(220,38,38,0.06)] p-2.5 space-y-2">
                  <div className="flex items-start gap-1.5 text-xs text-[color:var(--danger)]"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{plan.message}</span></div>
                  {(plan.missing || []).map((m) => (
                    <div key={m.field} data-testid="replace-missing-row" className="flex items-center gap-2 text-xs">
                      <span className="font-mono truncate flex-1 text-[color:var(--ink-2)]" title={(m.widgets || []).map((w) => w.title).join(", ")}>{m.field} <span className="text-[color:var(--ink-3)] font-sans">· {m.widgets?.length} widget(s)</span></span>
                      <ArrowRight className="w-3 h-3 text-[color:var(--ink-3)] shrink-0" />
                      <Select value={fieldMap[m.field] || ""} onValueChange={(v) => mapField(m.field, v)}>
                        <SelectTrigger data-testid={`replace-map-${m.field}`} className="h-7 w-44 text-xs bg-[color:var(--surface)]"><SelectValue placeholder="Map to field…" /></SelectTrigger>
                        <SelectContent>{newCols.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ))}
                  <div className="flex justify-end">
                    <Button data-testid="replace-recheck-btn" size="sm" variant="outline" className="h-7 text-xs" disabled={busy || !allMapped}
                      onClick={() => dryRun(target, fieldMap)}>Apply mappings &amp; re-check</Button>
                  </div>
                </div>
              )}
              {!plan.blocked && (
                <div data-testid="replace-source-ready" className="flex items-center gap-1.5 text-xs text-[color:var(--brand-mint)]"><CheckCircle2 className="w-3.5 h-3.5" />All fields resolve in {plan.new_name} — ready to replace.</div>
              )}
              {mappedRows.length > 0 && (
                <div className="max-h-40 overflow-auto rounded-lg bg-[color:var(--surface-2)] p-2 space-y-0.5">
                  {mappedRows.map(([from, to]) => (
                    <div key={from} className="flex items-center gap-2 text-[11px] font-mono text-[color:var(--ink-2)]">
                      <span className="truncate flex-1">{from}</span><ArrowRight className="w-3 h-3 text-[color:var(--ink-3)]" /><span className={"truncate flex-1 " + (from !== to ? "text-[color:var(--brand-ocean)]" : "")}>{to}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="replace-source-confirm" disabled={busy || !plan || plan.blocked} className="bg-[color:var(--brand-ocean)] text-white" onClick={confirm}>
            {busy ? "Working…" : "Replace source"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
